import { Map as ImmutableMap } from 'immutable'; // @ts-expect-error Untyped import

import CacheResult from '../records/CacheResult';
import defer from 'hs-promise-utils/defer';
import { throttle } from 'underscore';
const FIVE_MINUTES_AGO_IN_MILLISECONDS = 5 * 60 * 1000;
const cachedFunctionsArray = [];
let cache = ImmutableMap();
let cacheEnabled = true;

function getCacheKey(func) {
  let funcIndex = cachedFunctionsArray.indexOf(func);

  if (funcIndex < 0) {
    funcIndex = cachedFunctionsArray.push(func) - 1;
  }

  return funcIndex.toString();
}
/**
 * Written basically for tests, to allow us disable the cache while running tests which dispatch multiple times.
 * @param {Boolean} enableCache
 */


export function setCacheEnabled(enableCache) {
  cacheEnabled = enableCache;
}
export function invalidateCache() {
  cache = ImmutableMap();
}
/**
 * Before making a request, this method will check to see if that request was made previously and within an acceptable timeframe.
 * If so, we should return a promise which resolves to the past result. Otherwise, we will hit the preflight callback and then
 * make the request, returning the promise
 * @param {function} apiPromise - a function which makes a request, returning a promise
 * @param {function} preflightCallback - a function to call before we make call apiPromise
 * @param {object} params - the arguments we want to pass into the apiPromise method.
 * @returns {Promise}
 */
// todo: implement limit in size of the cache

export function shouldFetchWithCache(apiPromise, preflightCallback, params) {
  const callerKey = getCacheKey(apiPromise);
  const cacheCode = ImmutableMap(params).hashCode().toString();
  const maybeCacheHit = cache.getIn([callerKey, 'cacheByParams', cacheCode]);
  const callStartTime = Date.now();
  preflightCallback();

  if (maybeCacheHit && cacheEnabled) {
    const millisSinceLastValidResult = callStartTime - maybeCacheHit.get('timestamp');
    const cacheHitIsUpToDate = millisSinceLastValidResult < FIVE_MINUTES_AGO_IN_MILLISECONDS;

    if (cacheHitIsUpToDate) {
      cache = cache.setIn([callerKey, 'lastCallStart'], callStartTime);
      return Promise.resolve({
        result: maybeCacheHit.get('result'),
        timestamp: maybeCacheHit.get('timestamp')
      });
    }
  }

  cache = cache.mergeDeep({
    [callerKey]: {
      lastCallStart: callStartTime,
      cacheByParams: {
        [cacheCode]: CacheResult({
          isFetching: true
        })
      }
    }
  });
  const deferred = defer();
  apiPromise(params).then(result => {
    const timestamp = Date.now(); // proceed only if the call is still relevant

    if (cache.getIn([callerKey, 'lastCallStart']) === callStartTime) {
      cache = cache.setIn([callerKey, 'cacheByParams', cacheCode], CacheResult({
        timestamp,
        result
      }));
      deferred.resolve({
        result,
        timestamp
      });
    }
  }).catch(deferred.reject);
  return deferred.promise;
}
export function shouldFetch(state, lang) {
  if (!state) {
    return true;
  }

  const isFetching = state.get('isRequesting');
  const lastValidResult = state.get('lastValidResult');

  if (isFetching) {
    return false;
  }

  if (!lastValidResult) {
    return true;
  }

  if (state.get('requestLanguage') && state.get('requestLanguage').toUpperCase() !== lang.toUpperCase()) {
    return true;
  }

  const millisSinceLastValidResult = Date.now() - lastValidResult;
  return millisSinceLastValidResult > FIVE_MINUTES_AGO_IN_MILLISECONDS;
}
export function reduceListToMapById(list) {
  return list.reduce((map, thisItem) => map.set(thisItem.get('id'), thisItem), ImmutableMap());
}
export const makeDebouncedAction = (...params) => {
  const debouncedFn = throttle(...params);
  return () => debouncedFn;
};